// Hook para gestionar pagos desde el panel administrativo
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../utils/toastHelper'

import { ApiClient, apiClient } from '../services/api'
import { QUERY_KEYS as GLOBAL_QUERY_KEYS } from '../config/constants'

// Interfaces
export interface Pago {
  id: number
  reserva_id: string
  cliente_nombre?: string  // Nombre del cliente de la reserva
  espacios_nombres?: string  // Nombres de espacios separados por coma
  monto: number
  moneda: string
  proveedor_pago: string
  estado: 'aprobado' | 'pendiente' | 'rechazado'
  metadatos?: Record<string, any>
  fecha_pago?: string
  fecha_creacion: string
  fecha_actualizacion: string
  // Campos de comprobante de pago
  comprobante_path?: string
  comprobante_filename?: string
  comprobante_mime_type?: string
  comprobante_size?: number
  comprobante_upload_date?: string
}

export interface PagosListResponse {
  total: number
  pagos: Pago[]
}

export interface PagosFiltros {
  estado?: string
  reserva_id?: string
  proveedor_pago?: string
  fecha_desde?: string
  fecha_hasta?: string
  buscar?: string
  limit?: number
  offset?: number
}

// Query keys
export const QUERY_KEYS = {
  PAGOS_ADMIN: 'pagos-admin',
  PAGOS_BY_RESERVA: 'pagos-by-reserva',
  PAGO_DETAIL: 'pago-detail',
} as const

// Hook para listar pagos con filtros
export const usePagosAdmin = (filtros: PagosFiltros) => {
  return useQuery({
    queryKey: [QUERY_KEYS.PAGOS_ADMIN, filtros],
    queryFn: async () => {
      const params = new URLSearchParams()

      if (filtros.estado) params.append('estado', filtros.estado)
      if (filtros.proveedor_pago) params.append('proveedor_pago', filtros.proveedor_pago)
      if (filtros.fecha_desde) params.append('fecha_desde', filtros.fecha_desde)
      if (filtros.fecha_hasta) params.append('fecha_hasta', filtros.fecha_hasta)
      if (filtros.buscar) params.append('buscar', filtros.buscar)
      if (filtros.limit) params.append('limit', filtros.limit.toString())
      if (filtros.offset) params.append('offset', filtros.offset.toString())

      return await ApiClient.get<PagosListResponse>(
        `/api/payments/admin/list?${params.toString()}`
      )
    },
    staleTime: 30 * 1000, // 30 segundos
  })
}

// Hook para obtener pagos de una reserva específica
export const usePagosPorReserva = (reservaId: string | null) => {
  return useQuery({
    queryKey: [QUERY_KEYS.PAGOS_BY_RESERVA, reservaId],
    queryFn: async () => {
      if (!reservaId) return []
      return await ApiClient.get<Pago[]>(
        `/api/admin-protected/reservas/${reservaId}/pagos`
      )
    },
    enabled: !!reservaId,
    staleTime: 30 * 1000,
  })
}

// Hook para actualizar estado de pago
export const useActualizarPago = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { pagoId: number; estado: string }) => {
      return await ApiClient.patch<Pago>(
        `/api/admin-protected/pagos/${params.pagoId}`,
        { estado: params.estado }
      )
    },
    onSuccess: (data) => {
      toastSuccess('Pago actualizado exitosamente')
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAGOS_ADMIN],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAGOS_BY_RESERVA],
      })
    },
    onError: (error: any) => {
      toastError(error.response?.data?.detail || 'Error al actualizar el pago')
    },
  })
}

// Hook para eliminar un pago manual
export interface EliminarPagoParams {
  pagoId: number
  reservaId: string
}

export const useEliminarPago = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: EliminarPagoParams) => {
      return await ApiClient.delete(
        `/api/admin-protected/pagos-manuales/${params.reservaId}/pagos/${params.pagoId}`
      )
    },
    onSuccess: (_, variables) => {
      toastSuccess('Pago eliminado exitosamente')
      // Invalidar queries de pagos
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAGOS_BY_RESERVA, variables.reservaId],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAGOS_ADMIN],
      })
      // Invalidar query de reservas (para actualizar la lista)
      queryClient.invalidateQueries({
        queryKey: GLOBAL_QUERY_KEYS.RESERVAS,
      })
    },
    onError: (error: any) => {
      toastError(error.response?.data?.detail || 'Error al eliminar el pago')
    },
  })
}

// Hook para editar un pago manual
export interface EditarPagoParams {
  monto: number
  metodo_pago?: string
  referencia_pago?: string | null
  metadata?: Record<string, any>
}

export const useEditarPago = (reservaId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { pagoId: number; datos: EditarPagoParams }) => {
      const response = await apiClient.put(
        `/api/admin-protected/pagos-manuales/${reservaId}/pagos/${params.pagoId}`,
        params.datos
      )
      return response.data
    },
    onSuccess: () => {
      toastSuccess('Pago actualizado exitosamente')
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAGOS_BY_RESERVA, reservaId],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAGOS_ADMIN],
      })
    },
    onError: (error: any) => {
      toastError(error.response?.data?.detail || 'Error al actualizar el pago')
    },
  })
}

// Hook para crear un nuevo pago manual
export interface CrearPagoParams {
  monto: number
  metodo_pago: string
  referencia_pago?: string
  comprobante_pago?: string
  metadata?: Record<string, any>
}

export const useCrearPago = (reservaId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (datos: CrearPagoParams) => {
      return await ApiClient.post(
        `/api/admin-protected/pagos-manuales/${reservaId}/registrar`,
        {
          reserva_id: reservaId,
          ...datos
        }
      )
    },
    onSuccess: () => {
      toastSuccess('Pago registrado exitosamente')
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAGOS_BY_RESERVA, reservaId],
      })
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAGOS_ADMIN],
      })
    },
    onError: (error: any) => {
      toastError(error.response?.data?.detail || 'Error al registrar el pago')
    },
  })
}
